import { reviewAskCopy } from './review-ask-copy';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { dunningSelector, selectUserId } from '@common-reducers/UserSelectors';
import {
  useGetNudgeQuery,
  useRecordNudgeEventMutation,
} from '@common-reducers/backend-api/backend-api';
import { dismissNudge, selectNudgeDismissed } from '@common-reducers/nudge-slice';
import { namespace } from '@common/config/constants';
import { useReviewAskBlocker, useReviewAskBlockers } from './review-ask-blockers';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { NudgeClientEventName } from '@bt-common/nudges/nudge.types';
import {
  buildNudgeEventProps,
  isReviewAskRenderable,
  shouldRenderNudge,
} from './nudge-banner-logic';
import type { NudgePayload } from './nudge-banner-logic';

type Props = {
  campaignId: string;
  /** The finished-campaign screen is showing an error state: the ask stays out of the way. */
  hasError?: boolean;
};

/**
 * #787: post-campaign review card on the finished-campaign summary. The server decides eligibility
 * (finished campaign with 20+ recipients and 90%+ delivered, first one only, shared 90-day / 2-lifetime cap, pilot flag); this
 * only renders what it serves. Fail-closed: any error / no payload => nothing.
 */
const ReviewAskCard: React.FC<Props> = ({ campaignId, hasError = false }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);
  const { isDunning } = useAppSelector(dunningSelector);
  const { data } = useGetNudgeQuery({ placement: 'campaign_finish', campaignId }, { skip: !userId });
  const [recordNudgeEvent] = useRecordNudgeEventMutation();
  useReviewAskBlocker('error', hasError);
  const blockers = useReviewAskBlockers();

  const raw = data?.nudge ?? null;
  const payload: NudgePayload | null = raw && raw.nudgeId && raw.cta ? raw : null;
  const dismissed = useAppSelector((s) => selectNudgeDismissed(s, payload?.nudgeId));
  const visible =
    !dismissed &&
    isReviewAskRenderable(payload, 'card', { blockers }) &&
    shouldRenderNudge(!!isDunning, payload, 'extension');

  const shownRef = useRef(false);
  const fireEvent = (name: NudgeClientEventName, p: NudgePayload) => {
    try {
      recordNudgeEvent({ name, props: buildNudgeEventProps(p, 'extension') }).catch(() => { /* never break the card */ });
    } catch {
      // never break the card
    }
  };

  useEffect(() => {
    if (!visible || !payload || shownRef.current) return;
    shownRef.current = true;
    fireEvent('nudge_shown', payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible || !payload) return null;
  if (payload.nudgeId !== 'review_ask_campaign_2026q4') return null;

  const copy = reviewAskCopy(t, payload.nudgeId, payload);
  const onWrite = () => {
    fireEvent('nudge_clicked', payload);
    try {
      window.open(payload.cta.url, '_blank', 'noopener,noreferrer');
    } catch {
      // never break the card
    }
    dispatch(dismissNudge(payload.nudgeId));
  };
  const onNotNow = () => {
    fireEvent('nudge_dismissed', payload);
    dispatch(dismissNudge(payload.nudgeId));
  };

  return (
    <div
      data-testid="review-ask-card"
      className={cn(
        'w-full mb-6 rounded-lg border border-blue-200 dark:border-blue-800',
        'bg-blue-50 dark:bg-blue-950/40 p-4 flex items-center gap-4 shadow-sm'
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-blue-900 dark:text-blue-100">
          {copy.headline}
        </div>
        <div className="text-xs text-blue-800 dark:text-blue-200">
          {copy.body}
        </div>
      </div>
      <Button size="sm" onClick={onWrite}>{t('reviewAsk.write', { defaultValue: payload.cta.label })}</Button>
      <Button size="sm" variant="outline" onClick={onNotNow}>{t('reviewAsk.notNow', { defaultValue: 'Not now' })}</Button>
    </div>
  );
};

export default ReviewAskCard;
